import {
  AccountProfile,
  DeviceRow,
  EpochMillis,
  passwordUpdatedAt,
  toAccountProfile,
  toDeviceRows,
} from "core"
import React, { useCallback, useEffect, useState } from "react"
import {
  DeleteAllSessions,
  DeleteSession,
  GetAccountInitialData,
  GetDevices,
} from "../helpers/api"

/** One flag per request, so a single outage only degrades its own card. */
export interface AccountErrors {
  profile: boolean
  password: boolean
  devices: boolean
}

export interface AccountContextType {
  profile: AccountProfile | null
  passwordUpdatedAt: EpochMillis | null
  deviceRows: DeviceRow[]
  loading: boolean
  errors: AccountErrors
  signOutSession: (sessionId: string) => Promise<void>
  signOutEverywhere: () => Promise<void>
}

const noErrors: AccountErrors = {
  profile: false,
  password: false,
  devices: false,
}

export const AccountContext = React.createContext<AccountContextType>({
  profile: null,
  passwordUpdatedAt: null,
  deviceRows: [],
  loading: false,
  errors: noErrors,
  signOutSession: async () => undefined,
  signOutEverywhere: async () => undefined,
})

interface AccountContextProviderProps {
  children: React.ReactNode
  onSignedOutEverywhere: () => void
}

const AccountContextProvider = ({
  children,
  onSignedOutEverywhere,
}: AccountContextProviderProps) => {
  const [profile, setProfile] = useState<AccountProfile | null>(null)
  const [passwordChangedAt, setPasswordChangedAt] =
    useState<EpochMillis | null>(null)
  const [deviceRows, setDeviceRows] = useState<DeviceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<AccountErrors>(noErrors)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const { account, credentials, devices } = await GetAccountInitialData()
      if (cancelled) return

      setProfile(
        account.status === "fulfilled" ? toAccountProfile(account.value) : null
      )
      setPasswordChangedAt(
        credentials.status === "fulfilled"
          ? passwordUpdatedAt(credentials.value)
          : null
      )
      setDeviceRows(
        devices.status === "fulfilled" ? toDeviceRows(devices.value) : []
      )
      setErrors({
        profile: account.status === "rejected",
        password: credentials.status === "rejected",
        devices: devices.status === "rejected",
      })
      setLoading(false)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  // Refetching keeps the rows honest without reconciling local state, which is
  // where the previous delete-by-id-but-splice-by-index bug came from. Signing
  // out the current session is the exception: refetching would 401, so hand off
  // to logout instead.
  const signOutSession = useCallback(
    async (sessionId: string) => {
      const isCurrent = deviceRows.some(
        (row) => row.sessionId === sessionId && row.isCurrent
      )

      try {
        await DeleteSession(sessionId)

        if (isCurrent) {
          onSignedOutEverywhere()
          return
        }

        setDeviceRows(toDeviceRows(await GetDevices()))
        setErrors((previous) => ({ ...previous, devices: false }))
      } catch (error) {
        setErrors((previous) => ({ ...previous, devices: true }))
      }
    },
    [deviceRows, onSignedOutEverywhere]
  )

  const signOutEverywhere = useCallback(async () => {
    try {
      await DeleteAllSessions()
      onSignedOutEverywhere()
    } catch (error) {
      setErrors((previous) => ({ ...previous, devices: true }))
    }
  }, [onSignedOutEverywhere])

  return (
    <AccountContext.Provider
      value={{
        profile,
        passwordUpdatedAt: passwordChangedAt,
        deviceRows,
        loading,
        errors,
        signOutSession,
        signOutEverywhere,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export default AccountContextProvider
